import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MessageService, LazyLoadEvent } from 'primeng/api';
import { helperService } from 'src/app/utils/helper';
import { FieldInfoGridService } from './field-info-grid.service';

@Component({
    selector: 'field-info-grid',
    templateUrl: './field-info-grid.component.html',
    styleUrls: ['./field-info-grid.component.scss'],
    standalone: false
})
export class FieldInfoGridComponent implements OnInit {
  fieldRecords: any[] = [];
  cols: any[] = [];
  fieldDialog = false;
  viewOnly = false;
  title = '';
  loading = false;
  totalRecords = 0;
  first = 1;
  submitted = false;
  btnLoading = false;
  lastTableLazyLoadEvent: any = null;
  fieldLoadingSubscription: any = null;
  selectedField: any = null;
  fieldForm: UntypedFormGroup;
  defaultFieldForm: any = {};

  constructor(
    private fieldInfoService: FieldInfoGridService,
    private readonly messageService: MessageService,
    private filterService: helperService,
    private cdr: ChangeDetectorRef
  ) {
    this.fieldForm = new UntypedFormGroup({
      id: new UntypedFormControl({ value: '', disabled: true }),
      gridName: new UntypedFormControl({ value: '', disabled: true }),
      fieldName: new UntypedFormControl({ value: '', disabled: true }),
      fieldKeyName: new UntypedFormControl({ value: '', disabled: true }),
      informationText: new UntypedFormControl(),
      showInformation: new UntypedFormControl()
    });
  }

  ngOnInit(): void {
    this.initTableCols();
    this.defaultFieldForm = this.fieldForm.value;
    this.loading = true;
    this.loadFieldRecords({ first: 1, rows: 10 });
  }

  private initTableCols() {
    this.cols = [
      { field: 'fieldName', header: 'Field Name', isSortable: true, isFilterable: true },
      { field: 'gridName', header: 'Screen Name', isSortable: true, isFilterable: true }
    ];
  }

  loadFieldRecords(event: LazyLoadEvent) {
    event = event ? event : this.lastTableLazyLoadEvent || { first: 1, rows: 10 };
    this.lastTableLazyLoadEvent = event;
    this.loading = true;
    event.first = event.first || 1;
    event.rows = event.rows || 10;
    let payload = this.filterService.generateEventPayload(event);

    if (this.fieldLoadingSubscription) {
      this.fieldLoadingSubscription.unsubscribe();
    }

    this.fieldLoadingSubscription = this.fieldInfoService.getFieldInfoList(Math.floor(event.first / event.rows + 1), event.rows, payload)
      .subscribe(res => {
        this.fieldRecords = res?.data || [];
        this.totalRecords = res?.customValues?.totalNumberOfRecords || this.fieldRecords.length;
        this.loading = false;
      }, err => {
        this.fieldRecords = [];
        this.totalRecords = 0;
        this.loading = false;
      });
  }

  viewField(field: any) {
    this.fieldForm.patchValue(field);
    this.fieldForm.disable();
    this.fieldDialog = true;
    this.viewOnly = true;
    this.title = 'View Field Information';
  }

  editField(field: any) {
    this.fieldForm.patchValue(field);
    this.fieldForm.get('informationText')?.enable();
    this.fieldForm.get('showInformation')?.enable();
    // Hide fieldKeyName in edit (do not show in dialog)
    this.fieldDialog = true;
    this.viewOnly = false;
    this.title = 'Edit Field Information';
  }

  saveField() {
    if (this.btnLoading) return;
    this.submitted = true;
    this.messageService.clear();

    if (this.fieldForm.get('showInformation')?.value === 'Yes' && !this.fieldForm.get('informationText')?.value) {
      this.messageService.add({ severity: 'error', detail: 'Information Text is required when Show Information is enabled' });
      return;
    }

    if (this.fieldForm.valid) {
      this.btnLoading = true;
      this.fieldForm.disable();
      const payload = {
        informationText: this.fieldForm.get('informationText')?.value,
        showInformation: this.fieldForm.get('showInformation')?.value
      };

      this.fieldInfoService.saveAndRefresh(payload, this.fieldForm.get('id')?.value).then(
        res => {
          this.btnLoading = false;
          if (res.message) {
            this.messageService.add({ severity: 'success', detail: 'Field information updated successfully' });
            this.fieldDialog = false;
            this.loadFieldRecords(this.lastTableLazyLoadEvent);
          } else {
            this.messageService.add({ severity: 'error', detail: res.message });
          }
          this.fieldForm.enable();
        },
        error => {
          this.btnLoading = false;
          this.messageService.add({ severity: 'error', detail: error.message });
          this.fieldForm.enable();
        }
      );
    }
  }

  getUpdateDisabledReason(): string {
    if (this.btnLoading) {
      return 'Saving in progress, please wait…';
    }
    return '';
  }

  closeDialog() {
    this.fieldDialog = false;
    this.fieldForm.reset(this.defaultFieldForm);
    this.fieldForm.enable();
    this.fieldForm.get('id')?.disable();
    this.fieldForm.get('gridName')?.disable();
    this.fieldForm.get('fieldName')?.disable();
    this.fieldForm.get('fieldKeyName')?.disable();
    this.selectedField = null;
    this.viewOnly = false;
    this.title = '';
    this.cdr.detectChanges();
  }

  // Form control getters for template usage
  get id() { return this.fieldForm.get('id'); }
  get gridName() { return this.fieldForm.get('gridName'); }
  get fieldName() { return this.fieldForm.get('fieldName'); }
  get fieldKeyName() { return this.fieldForm.get('fieldKeyName'); }
  get informationText() { return this.fieldForm.get('informationText'); }
  get showInformation() { return this.fieldForm.get('showInformation'); }
}
